/**
 * CoachPlaybook - Roster Component
 * Version: 0.6.0
 * Description: ESPN broadcast-quality team roster with sport-aware position filters,
 *              dark glass panels, and responsive grid layout
 */

import React, { useState, useEffect, useMemo } from 'react';
import PlayerCard from './PlayerCard';
import PlayerForm from './PlayerForm';
import JerseyIcon, { DEFAULT_JERSEY_TEMPLATE } from '../common/JerseyIcon';
import { getSetting } from '../../utils/db';
import { useSport } from '../common/SportContext';
import { useTheme } from '../../utils/theme';
import {
  getAllPlayers,
  addPlayer,
  updatePlayer,
  deletePlayer,
  searchPlayers,
  filterPlayersByPosition,
  sortByNumber,
  sortByName,
  sortByPosition,
  POSITIONS
} from '../../utils/players';

const ROSTER_FIELDS_KEY = 'rosterFieldSettings';

const DEFAULT_FIELD_SETTINGS = {
  showHeight: true,
  showGrade: true,
  showGraduationYear: true,
  showNotes: true
};

const FONT = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const Roster = ({ teamName = '', jerseyTemplates = [], activeTemplateId = 'default' }) => {
  const { mode, theme } = useTheme();

  // ── Inline style constants derived from theme ───────────────────────
  const COLORS = useMemo(() => ({
    bg: theme.bg,
    panel: theme.bgPanel,
    card: theme.bgCard,
    cardHover: mode === 'dark' ? '#263548' : '#e2e8f0',
    border: theme.border,
    borderLight: theme.borderLight,
    textPrimary: theme.text,
    textSecondary: theme.textSecondary,
    textMuted: theme.textMuted,
    accent: theme.accent,
    accentGradientStart: theme.accent,
    accentGradientEnd: mode === 'dark' ? '#818cf8' : '#6366f1',
    glass: theme.glass,
    glassBorder: theme.glassBorder,
    danger: theme.danger,
    dangerHover: mode === 'dark' ? '#dc2626' : '#b91c1c',
    success: theme.success,
    successBg: `${theme.success}1f`,
    errorBg: `${theme.danger}1f`,
  }), [theme, mode]);
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState('all');
  const [sortMode, setSortMode] = useState('number');
  const [showForm, setShowForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState(null);
  const [message, setMessage] = useState(null);
  const [fieldSettings, setFieldSettings] = useState(DEFAULT_FIELD_SETTINGS);
  const { currentSport, sportConfig } = useSport();

  // Sport-specific positions from config
  const sportPositions = useMemo(() => {
    return sportConfig?.positions || [];
  }, [sportConfig]);

  // Position color map from sport config
  const positionColorMap = useMemo(() => {
    const map = {};
    sportPositions.forEach(pos => {
      map[pos.id] = pos.defaultColor || COLORS.accent;
    });
    return map;
  }, [sportPositions]);

  // Load players and field settings
  useEffect(() => {
    loadData();
  }, [currentSport]);

  // Apply filters + sort
  useEffect(() => {
    let result = players;
    result = searchPlayers(result, searchTerm);
    result = filterPlayersByPosition(result, positionFilter);

    switch (sortMode) {
      case 'number':
        result = sortByNumber(result);
        break;
      case 'name':
        result = sortByName(result);
        break;
      case 'position':
        result = sortByPosition(result);
        break;
      default:
        break;
    }

    setFilteredPlayers(result);
  }, [players, searchTerm, positionFilter, sortMode]);

  const loadData = async () => {
    setLoading(true);
    const loadedPlayers = await getAllPlayers(currentSport);
    setPlayers(loadedPlayers);

    const savedSettings = await getSetting(ROSTER_FIELDS_KEY);
    if (savedSettings) {
      setFieldSettings({ ...DEFAULT_FIELD_SETTINGS, ...savedSettings });
    }

    setLoading(false);
  };

  const handleAddPlayer = () => {
    setEditingPlayer(null);
    setShowForm(true);
  };

  const handleEditPlayer = (player) => {
    setEditingPlayer(player);
    setShowForm(true);
  };

  const handleSavePlayer = async (playerData) => {
    let result;

    if (editingPlayer) {
      result = await updatePlayer(editingPlayer.id, playerData, currentSport);
      showMessage(result.success ? 'Player updated successfully' : result.error, result.success ? 'success' : 'error');
    } else {
      result = await addPlayer(playerData, currentSport);
      showMessage(result.success ? 'Player added successfully' : result.error, result.success ? 'success' : 'error');
    }

    if (result.success) {
      await loadData();
      setShowForm(false);
      setEditingPlayer(null);
    }
  };

  const handleDeleteClick = (player) => {
    setPlayerToDelete(player);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (playerToDelete) {
      const result = await deletePlayer(playerToDelete.id);
      showMessage(result.success ? 'Player deleted' : result.error, result.success ? 'success' : 'error');

      if (result.success) {
        await loadData();
      }

      setShowDeleteConfirm(false);
      setPlayerToDelete(null);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  // Active jersey template
  const activeTemplate = jerseyTemplates.find(t => t.isDefault)
    || jerseyTemplates.find(t => t.id === activeTemplateId)
    || jerseyTemplates[0]
    || DEFAULT_JERSEY_TEMPLATE;

  // Get position color for a player
  const getPositionColor = (positionId) => positionColorMap[positionId] || COLORS.accent;

  // ── Loading state ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: COLORS.bg,
        fontFamily: FONT
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 48,
            height: 48,
            border: `3px solid ${COLORS.accent}`,
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: COLORS.textSecondary, fontSize: 14 }}>Loading roster...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────
  return (
    <div
      data-element="roster-root"
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: COLORS.bg,
        fontFamily: FONT,
        overflow: 'hidden'
      }}
    >
      {/* ── Keyframes ─────────────────────────────────────────────── */}
      <style>{`
        @keyframes rosterFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes rosterCardIn {
          from { opacity: 0; transform: scale(0.96) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes rosterPulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.7; }
        }
        @keyframes toastSlide {
          from { opacity: 0; transform: translateY(-12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* ── Header Bar ────────────────────────────────────────────── */}
      <div
        data-element="roster-header"
        style={{
          background: `linear-gradient(135deg, ${COLORS.panel} 0%, rgba(15, 23, 42, 0.9) 100%)`,
          borderBottom: `1px solid ${COLORS.border}`,
          padding: '20px 24px 16px',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          animation: 'rosterFadeIn 0.4s ease-out'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16
        }}>
          {/* Left: Sport icon + title + count */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {/* Sport icon */}
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: `linear-gradient(135deg, ${sportConfig?.accentColor || COLORS.accent}22, ${sportConfig?.accentColor || COLORS.accent}08)`,
              border: `1px solid ${sportConfig?.accentColor || COLORS.accent}33`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22
            }}>
              {sportConfig?.icon || '🏀'}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h1
                  data-element="roster-header-title"
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: COLORS.textPrimary,
                    margin: 0,
                    letterSpacing: '-0.02em'
                  }}
                >
                  Team Roster
                </h1>
                {/* Player count badge */}
                <span
                  data-element="roster-header-count"
                  style={{
                    background: `linear-gradient(135deg, ${COLORS.accentGradientStart}18, ${COLORS.accentGradientEnd}18)`,
                    border: `1px solid ${COLORS.accent}33`,
                    color: COLORS.accent,
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '3px 10px',
                    borderRadius: 20,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase'
                  }}
                >
                  {players.length} player{players.length !== 1 ? 's' : ''}
                </span>
              </div>
              <p style={{
                fontSize: 12,
                color: COLORS.textMuted,
                margin: '2px 0 0',
                fontFamily: "'IBM Plex Mono', monospace",
                letterSpacing: '0.04em'
              }}>
                {sportConfig?.name || 'Basketball'} {teamName ? `/ ${teamName}` : ''}
              </p>
            </div>
          </div>

          {/* Add Player button */}
          <button
            data-element="roster-add-btn"
            onClick={handleAddPlayer}
            style={{
              background: `linear-gradient(135deg, ${COLORS.accentGradientStart}, ${COLORS.accentGradientEnd})`,
              color: '#0f172a',
              border: 'none',
              borderRadius: 10,
              padding: '10px 20px',
              fontSize: 13,
              fontWeight: 700,
              fontFamily: FONT,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: `0 4px 16px ${COLORS.accent}33`,
              transition: 'all 0.2s ease',
              letterSpacing: '0.02em'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = `0 6px 24px ${COLORS.accent}44`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `0 4px 16px ${COLORS.accent}33`;
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span>Add Player</span>
          </button>
        </div>

        {/* ── Search & Filters Row ────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Search input + sort dropdown */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {/* Search */}
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                data-element="roster-search-input"
                type="text"
                placeholder="Search players..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px 10px 40px',
                  background: COLORS.glass,
                  border: `1px solid ${COLORS.glassBorder}`,
                  borderRadius: 10,
                  color: COLORS.textPrimary,
                  fontSize: 13,
                  fontFamily: FONT,
                  outline: 'none',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  transition: 'border-color 0.2s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={e => { e.target.style.borderColor = `${COLORS.accent}55`; }}
                onBlur={e => { e.target.style.borderColor = COLORS.glassBorder; }}
              />
              <svg
                style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }}
                width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLORS.textSecondary} strokeWidth="2" strokeLinecap="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
            </div>

            {/* Sort dropdown */}
            <select
              data-element="roster-sort-select"
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              style={{
                padding: '10px 32px 10px 12px',
                background: COLORS.glass,
                border: `1px solid ${COLORS.glassBorder}`,
                borderRadius: 10,
                color: COLORS.textPrimary,
                fontSize: 13,
                fontFamily: FONT,
                outline: 'none',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 10px center',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                minWidth: 120
              }}
            >
              <option value="number" style={{ background: COLORS.panel }}>Sort: Number</option>
              <option value="name" style={{ background: COLORS.panel }}>Sort: Name</option>
              <option value="position" style={{ background: COLORS.panel }}>Sort: Position</option>
            </select>
          </div>

          {/* Position filter pills */}
          <div
            data-element="roster-position-pills"
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              alignItems: 'center'
            }}
          >
            {/* "All" pill */}
            <button
              onClick={() => setPositionFilter('all')}
              style={{
                padding: '5px 14px',
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 600,
                fontFamily: FONT,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                letterSpacing: '0.03em',
                border: positionFilter === 'all'
                  ? `1.5px solid ${COLORS.accent}`
                  : `1.5px solid ${COLORS.glassBorder}`,
                background: positionFilter === 'all'
                  ? `${COLORS.accent}22`
                  : 'transparent',
                color: positionFilter === 'all'
                  ? COLORS.accent
                  : COLORS.textMuted
              }}
            >
              All
            </button>

            {/* Sport-specific position pills */}
            {sportPositions.map(pos => {
              const isActive = positionFilter === pos.id;
              const color = pos.defaultColor || COLORS.accent;
              return (
                <button
                  key={pos.id}
                  onClick={() => setPositionFilter(pos.id)}
                  style={{
                    padding: '5px 14px',
                    borderRadius: 20,
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: FONT,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    letterSpacing: '0.03em',
                    border: isActive
                      ? `1.5px solid ${color}`
                      : `1.5px solid ${COLORS.glassBorder}`,
                    background: isActive
                      ? `${color}22`
                      : 'transparent',
                    color: isActive
                      ? color
                      : COLORS.textMuted
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = `${color}66`;
                      e.currentTarget.style.color = color;
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = COLORS.glassBorder;
                      e.currentTarget.style.color = COLORS.textMuted;
                    }
                  }}
                >
                  {pos.short}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Toast Message ─────────────────────────────────────────── */}
      {message && (
        <div
          data-element="roster-message"
          style={{
            margin: '12px 20px 0',
            padding: '10px 16px',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 500,
            fontFamily: FONT,
            animation: 'toastSlide 0.3s ease-out',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: message.type === 'success' ? COLORS.successBg : COLORS.errorBg,
            color: message.type === 'success' ? COLORS.success : COLORS.danger,
            border: `1px solid ${message.type === 'success' ? `${COLORS.success}33` : `${COLORS.danger}33`}`
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            {message.type === 'success'
              ? <path d="M20 6L9 17l-5-5" />
              : <><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></>
            }
          </svg>
          {message.text}
        </div>
      )}

      {/* ── Player Grid (scrollable) ──────────────────────────────── */}
      <div
        data-element="roster-player-list"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 20px 80px'
        }}
      >
        {filteredPlayers.length === 0 ? (
          /* ── Empty State ─────────────────────────────────────── */
          <div
            data-element="roster-empty-state"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '80px 20px',
              animation: 'rosterFadeIn 0.5s ease-out'
            }}
          >
            {/* Sport icon large */}
            <div style={{
              width: 80,
              height: 80,
              borderRadius: 24,
              background: `linear-gradient(135deg, ${sportConfig?.accentColor || COLORS.accent}15, ${sportConfig?.accentColor || COLORS.accent}05)`,
              border: `1px solid ${sportConfig?.accentColor || COLORS.accent}22`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 36,
              marginBottom: 20
            }}>
              {sportConfig?.icon || '🏀'}
            </div>

            <h3
              data-element="roster-empty-title"
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: COLORS.textPrimary,
                margin: '0 0 6px',
                letterSpacing: '-0.01em'
              }}
            >
              {players.length === 0 ? 'No players yet' : 'No players found'}
            </h3>
            <p
              data-element="roster-empty-message"
              style={{
                fontSize: 13,
                color: COLORS.textMuted,
                margin: '0 0 24px',
                textAlign: 'center',
                maxWidth: 280
              }}
            >
              {players.length === 0
                ? `Add your first ${sportConfig?.name?.toLowerCase() || 'basketball'} player to get started`
                : 'Try adjusting your search or filters'}
            </p>
            {players.length === 0 && (
              <button
                data-element="roster-empty-add-btn"
                onClick={handleAddPlayer}
                style={{
                  background: `linear-gradient(135deg, ${COLORS.accentGradientStart}, ${COLORS.accentGradientEnd})`,
                  color: '#0f172a',
                  border: 'none',
                  borderRadius: 10,
                  padding: '12px 28px',
                  fontSize: 14,
                  fontWeight: 700,
                  fontFamily: FONT,
                  cursor: 'pointer',
                  boxShadow: `0 4px 16px ${COLORS.accent}33`,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = `0 6px 24px ${COLORS.accent}44`;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 4px 16px ${COLORS.accent}33`;
                }}
              >
                Add First Player
              </button>
            )}
          </div>
        ) : (
          /* ── Player Cards Grid ──────────────────────────────── */
          <div
            data-element="roster-player-cards"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: 14
            }}
          >
            {filteredPlayers.map((player, index) => {
              const posColor = getPositionColor(player.position);
              const posConfig = sportPositions.find(p => p.id === player.position);

              return (
                <div
                  key={player.id}
                  data-element={`roster-grid-card-${player.id}`}
                  style={{
                    animation: `rosterCardIn 0.35s ease-out ${index * 0.04}s both`
                  }}
                >
                  {/* Glass card wrapper with position accent stripe */}
                  <div
                    style={{
                      position: 'relative',
                      background: COLORS.glass,
                      border: `1px solid ${COLORS.glassBorder}`,
                      borderRadius: 14,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                    }}
                    onClick={() => handleEditPlayer(player)}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = `0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px ${posColor}22`;
                      e.currentTarget.style.borderColor = `${posColor}33`;
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = COLORS.glassBorder;
                    }}
                  >
                    {/* Position color accent stripe (left edge) */}
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      background: `linear-gradient(180deg, ${posColor}, ${posColor}88)`,
                      borderRadius: '14px 0 0 14px'
                    }} />

                    {/* Card content */}
                    <div style={{ padding: '16px 16px 16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
                      {/* Jersey number (large bold) */}
                      <div style={{
                        minWidth: 52,
                        height: 52,
                        borderRadius: 12,
                        background: `${posColor}15`,
                        border: `1px solid ${posColor}25`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <span style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: posColor,
                          fontFamily: FONT,
                          letterSpacing: '-0.02em'
                        }}>
                          {player.jerseyNumber != null ? player.jerseyNumber : '--'}
                        </span>
                      </div>

                      {/* Player info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: COLORS.textPrimary,
                          letterSpacing: '-0.01em',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {player.firstName} {player.lastName}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                          {/* Position badge */}
                          <span style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: posColor,
                            background: `${posColor}18`,
                            border: `1px solid ${posColor}30`,
                            padding: '2px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                          }}>
                            {posConfig?.short || player.position || '—'}
                          </span>

                          {/* Height */}
                          {fieldSettings.showHeight && player.height && (
                            <span style={{
                              fontSize: 11,
                              color: COLORS.textMuted,
                              fontFamily: "'IBM Plex Mono', monospace"
                            }}>
                              {player.height}
                            </span>
                          )}

                          {/* Grade */}
                          {fieldSettings.showGrade && player.grade && (
                            <span style={{
                              fontSize: 11,
                              color: COLORS.textMuted
                            }}>
                              {player.grade}
                            </span>
                          )}
                        </div>

                        {/* Notes (truncated) */}
                        {fieldSettings.showNotes && player.notes && (
                          <p style={{
                            fontSize: 11,
                            color: COLORS.textMuted,
                            margin: '6px 0 0',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            opacity: 0.7
                          }}>
                            {player.notes}
                          </p>
                        )}
                      </div>

                      {/* Action buttons (desktop) */}
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        <button
                          data-element={`roster-card-edit-${player.id}`}
                          onClick={(e) => { e.stopPropagation(); handleEditPlayer(player); }}
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            border: 'none',
                            background: 'transparent',
                            color: COLORS.textMuted,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = `${COLORS.accent}18`;
                            e.currentTarget.style.color = COLORS.accent;
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = COLORS.textMuted;
                          }}
                          title="Edit player"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                          </svg>
                        </button>
                        <button
                          data-element={`roster-card-delete-${player.id}`}
                          onClick={(e) => { e.stopPropagation(); handleDeleteClick(player); }}
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            border: 'none',
                            background: 'transparent',
                            color: COLORS.textMuted,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = `${COLORS.danger}18`;
                            e.currentTarget.style.color = COLORS.danger;
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = COLORS.textMuted;
                          }}
                          title="Delete player"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Player Form Modal ─────────────────────────────────────── */}
      {showForm && (
        <div data-element="roster-player-form-modal">
          <PlayerForm
            player={editingPlayer}
            players={players}
            jerseyTemplates={jerseyTemplates}
            activeTemplateId={activeTemplateId}
            onSave={handleSavePlayer}
            onCancel={() => {
              setShowForm(false);
              setEditingPlayer(null);
              loadData();
            }}
          />
        </div>
      )}

      {/* ── Delete Confirmation Modal ─────────────────────────────── */}
      {showDeleteConfirm && playerToDelete && (
        <div
          data-element="roster-delete-modal"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 16,
            animation: 'rosterFadeIn 0.2s ease-out'
          }}
        >
          <div
            data-element="roster-delete-modal-content"
            style={{
              background: COLORS.panel,
              borderRadius: 16,
              padding: 28,
              maxWidth: 380,
              width: '100%',
              border: `1px solid ${COLORS.border}`,
              boxShadow: '0 24px 64px rgba(0, 0, 0, 0.6)',
              animation: 'rosterCardIn 0.25s ease-out'
            }}
          >
            {/* Warning icon */}
            <div style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              background: COLORS.errorBg,
              border: `1px solid ${COLORS.danger}25`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={COLORS.danger} strokeWidth="2" strokeLinecap="round">
                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </div>

            <h3
              data-element="roster-delete-title"
              style={{
                fontSize: 17,
                fontWeight: 700,
                color: COLORS.textPrimary,
                margin: '0 0 8px',
                textAlign: 'center'
              }}
            >
              Delete Player?
            </h3>
            <p
              data-element="roster-delete-message"
              style={{
                fontSize: 13,
                color: COLORS.textMuted,
                margin: '0 0 24px',
                textAlign: 'center',
                lineHeight: 1.5
              }}
            >
              Are you sure you want to delete <strong style={{ color: COLORS.textSecondary }}>
                {playerToDelete.firstName} {playerToDelete.lastName}
              </strong>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                data-element="roster-delete-cancel-btn"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setPlayerToDelete(null);
                }}
                style={{
                  flex: 1,
                  padding: '11px 16px',
                  background: COLORS.card,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 10,
                  color: COLORS.textSecondary,
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: FONT,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = COLORS.cardHover; }}
                onMouseLeave={e => { e.currentTarget.style.background = COLORS.card; }}
              >
                Cancel
              </button>
              <button
                data-element="roster-delete-confirm-btn"
                onClick={handleConfirmDelete}
                style={{
                  flex: 1,
                  padding: '11px 16px',
                  background: COLORS.danger,
                  border: 'none',
                  borderRadius: 10,
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: FONT,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  boxShadow: `0 4px 12px ${COLORS.danger}33`
                }}
                onMouseEnter={e => { e.currentTarget.style.background = COLORS.dangerHover; }}
                onMouseLeave={e => { e.currentTarget.style.background = COLORS.danger; }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roster;
